import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, IMAGES, imgUrl } from "../lib/api";
import { Play, Pause, Search, Shuffle, SkipForward, SkipBack, Volume2, Music2, User } from "lucide-react";

function formatTime(sec) {
  if (!sec || !isFinite(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function Music() {
  const audioRef = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [query, setQuery] = useState("");
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);

  useEffect(() => {
    Promise.all([
      api.get("/music").then((r) => r.data || []),
      api.get("/artists").then((r) => r.data || []).catch(() => []),
    ])
      .then(([t, a]) => { setTracks(t); setArtists(a); })
      .finally(() => setLoading(false));
  }, []);

  // Map track artist string -> matching artist record (substring, case-insensitive)
  const findArtistMatch = (trackArtist) => {
    if (!trackArtist || artists.length === 0) return null;
    const lower = trackArtist.toLowerCase();
    return artists.find((a) => a.name && lower.includes(a.name.toLowerCase())) || null;
  };

  // Artists matching the current search query (name / role / bio substring)
  const matchedArtists = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || artists.length === 0) return [];
    return artists.filter((a) =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.role || "").toLowerCase().includes(q) ||
      (a.bio || "").toLowerCase().includes(q)
    );
  }, [query, artists]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const indexed = tracks.map((t, i) => ({ ...t, origIdx: i }));
    if (!q) return indexed;
    return indexed.filter((t) => `${t.title} ${t.artist}`.toLowerCase().includes(q));
  }, [query, tracks]);

  const current = tracks[currentIdx];

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    const onTime = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => nextTrack();
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, shuffle, tracks.length]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (playing) {
      a.play().catch(() => setPlaying(false));
    } else {
      a.pause();
    }
  }, [playing, currentIdx, current]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const playAt = (idx) => {
    setCurrentIdx(idx);
    setPlaying(true);
  };
  const nextTrack = () => {
    if (tracks.length === 0) return;
    if (shuffle) {
      setCurrentIdx(Math.floor(Math.random() * tracks.length));
    } else {
      setCurrentIdx((i) => (i + 1) % tracks.length);
    }
    setPlaying(true);
  };
  const prevTrack = () => {
    if (tracks.length === 0) return;
    setCurrentIdx((i) => (i - 1 + tracks.length) % tracks.length);
    setPlaying(true);
  };
  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * duration;
  };

  return (
    <div className="bg-[#0A0A0A] text-white" data-testid="music-page">
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${IMAGES.texture})`, backgroundSize: "cover" }} />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 py-16">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#FCA311] mb-3">// THE CATALOG</div>
          <h1 className="font-display text-6xl sm:text-8xl leading-none flex items-center gap-4">
            <Music2 className="w-16 h-16 text-[#E63946]" />
            MUSIC.
          </h1>
          <p className="text-white/60 mt-4 max-w-xl">
            {tracks.length} tracks. Studio cuts, demos, collabs. Press play, stay locked.
          </p>
        </div>
      </section>

      {tracks.length > 0 && current && (
        <div className="sticky top-16 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/10" data-testid="music-player-bar">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-10 py-4">
            <div className="flex items-center gap-4">
              <button onClick={prevTrack} className="p-2 text-white/60 hover:text-white" aria-label="Previous" data-testid="music-prev"><SkipBack className="w-5 h-5" /></button>
              <button
                onClick={() => setPlaying((p) => !p)}
                className="flex-shrink-0 w-12 h-12 rounded-full bg-[#E63946] hover:bg-[#C92A36] text-white flex items-center justify-center transition-colors"
                data-testid="music-play-toggle"
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <button onClick={nextTrack} className="p-2 text-white/60 hover:text-white" aria-label="Next" data-testid="music-next"><SkipForward className="w-5 h-5" /></button>

              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-baseline">
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-lg truncate" data-testid="music-now-title">{current.title}</div>
                    <div className="font-mono text-[11px] text-white/50 truncate">{current.artist}</div>
                  </div>
                  <div className="font-mono text-[10px] text-white/40 ml-3 flex-shrink-0">
                    {formatTime(progress)} / {formatTime(duration)}
                  </div>
                </div>
                <button type="button" onClick={seek} className="mt-2 h-1.5 bg-white/10 w-full relative cursor-pointer hover:h-2 transition-all" aria-label="Seek">
                  <div className="absolute inset-y-0 left-0 bg-[#E63946]" style={{ width: duration ? `${(progress / duration) * 100}%` : "0%" }} />
                </button>
              </div>

              <button onClick={() => setShuffle((s) => !s)} className={`hidden sm:block p-2 ${shuffle ? "text-[#FCA311]" : "text-white/50 hover:text-white"}`} aria-label="Shuffle" data-testid="music-shuffle" title="Shuffle">
                <Shuffle className="w-5 h-5" />
              </button>

              <div className="hidden md:flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-white/50" />
                <input type="range" min="0" max="1" step="0.01" value={volume} onChange={(e) => setVolume(parseFloat(e.target.value))} className="w-20 accent-[#E63946]" aria-label="Volume" />
              </div>
            </div>

            <audio ref={audioRef} src={current.src} preload="metadata">
              <track kind="captions" />
            </audio>
          </div>
        </div>
      )}

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 py-8">
        <div className="relative mb-6 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tracks or artists..."
            className="w-full bg-[#151515] border border-white/10 focus:border-[#E63946] focus:outline-none pl-10 pr-4 py-3 font-mono text-sm"
            data-testid="music-search"
          />
        </div>

        {/* ARTIST MATCHES from playlist search */}
        {matchedArtists.length > 0 && (
          <div className="mb-6" data-testid="music-artist-matches">
            <div className="flex items-end gap-3 mb-3">
              <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">// ARTIST MATCHES</div>
              <div className="font-mono text-[10px] text-white/40">{matchedArtists.length} FOUND</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {matchedArtists.map((a) => (
                <Link
                  key={a.id}
                  to={`/artists?open=${a.id}`}
                  className="flex items-center gap-3 bg-[#151515] border border-white/10 hover:border-[#FCA311] transition-colors p-3 group"
                  data-testid={`music-artist-match-${a.id}`}
                >
                  <div className="w-14 h-14 bg-black border border-white/20 flex-shrink-0 overflow-hidden">
                    {a.photo_path ? (
                      <img src={imgUrl(a.photo_path)} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-display text-2xl text-white/30">
                        {a.name.slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] tracking-widest text-[#FCA311]">{a.role || "ARTIST"}</div>
                    <div className="font-display text-lg truncate group-hover:text-[#FCA311] transition-colors">{a.name}</div>
                    {a.bio && (
                      <p className="font-mono text-[10px] text-white/40 line-clamp-1">{a.bio}</p>
                    )}
                  </div>
                  <User className="w-4 h-4 text-white/30 group-hover:text-[#FCA311]" />
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="bg-[#151515] border border-white/10" data-testid="music-track-list">
          <div className="px-4 py-2 border-b border-white/10 grid grid-cols-[40px_1fr_auto] gap-3 font-mono text-[10px] tracking-widest text-white/40">
            <div>#</div>
            <div>TITLE / ARTIST</div>
            <div>PLAY</div>
          </div>
          {loading ? (
            <div className="px-4 py-8 text-center font-mono text-white/40">LOADING...</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-center font-mono text-white/40">{query ? "No matches." : "No tracks yet."}</div>
          ) : (
            filtered.map((t) => {
              const isActive = t.origIdx === currentIdx;
              const artistMatch = findArtistMatch(t.artist);
              return (
                <div
                  key={t.id}
                  onClick={() => playAt(t.origIdx)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); playAt(t.origIdx); } }}
                  role="button"
                  tabIndex={0}
                  className={`grid grid-cols-[40px_1fr_auto] gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors text-left items-center cursor-pointer ${isActive ? "bg-[#E63946]/10" : ""}`}
                  data-testid={`music-track-${t.origIdx}`}
                >
                  <div className={`font-mono text-xs ${isActive ? "text-[#E63946]" : "text-white/40"}`}>
                    {isActive && playing ? (
                      <div className="flex items-end gap-0.5 h-4">
                        <span className="w-0.5 bg-[#E63946] animate-pulse" style={{ height: "60%" }} />
                        <span className="w-0.5 bg-[#E63946] animate-pulse" style={{ height: "90%", animationDelay: "150ms" }} />
                        <span className="w-0.5 bg-[#E63946] animate-pulse" style={{ height: "40%", animationDelay: "300ms" }} />
                      </div>
                    ) : (
                      String(t.origIdx + 1).padStart(3, "0")
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className={`font-display text-base sm:text-lg truncate ${isActive ? "text-[#E63946]" : ""}`}>{t.title}</div>
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="font-mono text-[11px] text-white/50 truncate">{t.artist}</div>
                      {artistMatch && (
                        <Link
                          to={`/artists?open=${artistMatch.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-shrink-0 font-mono text-[9px] tracking-widest px-1.5 py-0.5 border border-[#FCA311]/40 text-[#FCA311] hover:bg-[#FCA311] hover:text-black transition-colors inline-flex items-center gap-1"
                          data-testid={`track-profile-link-${t.origIdx}`}
                          aria-label={`Open ${artistMatch.name} profile`}
                        >
                          <User className="w-2.5 h-2.5" /> PROFILE
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className={`text-xs font-mono ${isActive ? "text-[#FCA311]" : "text-white/30"}`}>
                    {isActive ? (playing ? "PLAYING" : "PAUSED") : "▶"}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="mt-6 font-mono text-[10px] tracking-widest text-white/40 text-center">
          {filtered.length} / {tracks.length} TRACKS
        </div>
      </section>
    </div>
  );
}
